import { useEffect, useMemo, useState } from "react";
import { Building2, ChevronDown, User } from "lucide-react";
import { Checkbox, Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";

export type OrganizacaoDoFiltro = { id: string; nome: string; pessoal?: boolean };

/**
 * Filtro de organizações do acesso pessoal (agenda, início, pacientes, prótese). Sem nada escolhido
 * mostra todas; escolher uma passa a mostrar só as escolhidas. A escolha fica no navegador.
 */
export function useOrganizacoesMarcadas(chave: string, todas: OrganizacaoDoFiltro[]) {
  const [escolhidas, setEscolhidas] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(chave) ?? "[]") as string[]);
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(chave, JSON.stringify([...escolhidas]));
    } catch {
      // sem storage (aba anônima): o filtro vale só nesta visita
    }
  }, [chave, escolhidas]);
  // Escolhida que sumiu da lista (saiu da organização) não conta: se não sobrar nenhuma, volta a todas.
  const selecionadas = useMemo(() => new Set(todas.filter((o) => escolhidas.has(o.id)).map((o) => o.id)), [todas, escolhidas]);
  const marcadas = useMemo(() => (selecionadas.size ? selecionadas : new Set(todas.map((o) => o.id))), [todas, selecionadas]);
  return {
    /** As organizações que aparecem na tela. */
    marcadas,
    controle: {
      selecionadas,
      onAlternar: (id: string) =>
        setEscolhidas(() => {
          const prox = new Set(selecionadas);
          if (prox.has(id)) prox.delete(id);
          else prox.add(id);
          return prox;
        }),
      onLimpar: () => setEscolhidas(new Set()),
    },
  };
}

/** Botão "Filtrar por organização" com a lista de caixas de marcar (Pessoal primeiro). */
export function FiltroDeOrganizacoes({
  organizacoes,
  selecionadas,
  onAlternar,
  onLimpar,
}: {
  organizacoes: OrganizacaoDoFiltro[];
  selecionadas: Set<string>;
  onAlternar: (id: string) => void;
  onLimpar: () => void;
}) {
  const ativo = selecionadas.size > 0;
  const rotulo = !ativo
    ? "Filtrar por organização"
    : selecionadas.size === 1
      ? organizacoes.find((o) => selecionadas.has(o.id))?.nome
      : `${selecionadas.size} organizações`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={
            ativo
              ? "inline-flex h-9 max-w-[280px] items-center gap-2 rounded-md border border-brand-200 bg-brand-50 px-3 text-[13px] font-medium text-brand-700 hover:bg-brand-100 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
              : "inline-flex h-9 max-w-[280px] items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 text-[13px] font-medium text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground"
          }
        >
          <Building2 className={ativo ? "h-4 w-4 shrink-0" : "h-4 w-4 shrink-0 text-zinc-400"} />
          <span className="truncate">{rotulo}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 border border-zinc-200 bg-white p-2 shadow-lg dark:border-border dark:bg-card">
        <div className="mb-1 flex items-center justify-between px-2 py-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Filtrar por organização</span>
          {ativo && (
            <button type="button" onClick={onLimpar} className="text-[11.5px] font-semibold text-brand-700 hover:underline dark:text-primary">
              Limpar filtro
            </button>
          )}
        </div>
        <ul className="max-h-72 overflow-auto">
          {organizacoes.map((o) => (
            <li key={o.id}>
              <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-zinc-800 hover:bg-zinc-50 dark:text-foreground dark:hover:bg-muted/40">
                <Checkbox checked={selecionadas.has(o.id)} onCheckedChange={() => onAlternar(o.id)} />
                {o.pessoal ? <User className="h-3.5 w-3.5 shrink-0 text-zinc-400" /> : <Building2 className="h-3.5 w-3.5 shrink-0 text-zinc-400" />}
                <span className="truncate">{o.nome}</span>
              </label>
            </li>
          ))}
        </ul>
        {!ativo && <p className="px-2 pb-1 pt-2 text-[11.5px] text-zinc-500">Sem filtro, aparecem todas.</p>}
      </PopoverContent>
    </Popover>
  );
}
